import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, BadgeCheck } from "lucide-react";
import { api, download, errMsg, fmtDate, fmtTime } from "../../lib/api";
import { PageHeader, Loading, StatusBadge, inputCls } from "../../components/common";

const dl = (path, name) => download(path, name).catch((e) => toast.error(errMsg(e)));

export default function Records() {
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [q, setQ] = useState("");
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (year && year.length !== 4) return undefined;
    const t = setTimeout(() => api.get("/records", { params: { year: year || undefined } }).then((r) => setRows(r.data)).catch((e) => { setRows([]); toast.error(errMsg(e)); }), 300);
    return () => clearTimeout(t);
  }, [year]);
  const list = (rows || []).filter((m) => (!q || `${m.title} ${m.code} ${m.organizer}`.toLowerCase().includes(q.toLowerCase())) && (!onlyMissing || !m.approved_minutes.length));
  return (
    <div>
      <PageHeader title="Meeting Records Archive" sub="Each closed or finalized meeting's official attendance report kept together with its approved minutes. Approve minutes in the meeting's Documents tab." />
      <div className="flex flex-wrap gap-2 mb-4">
        <input className={`${inputCls} h-10 w-auto max-w-[120px]`} placeholder="Year" value={year} onChange={(e) => setYear(e.target.value.replace(/\D/g, "").slice(0, 4))} data-testid="records-year" />
        <input className={`${inputCls} h-10 max-w-xs`} placeholder="Search meetings" value={q} onChange={(e) => setQ(e.target.value)} data-testid="records-search" />
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={onlyMissing} onChange={(e) => setOnlyMissing(e.target.checked)} data-testid="records-only-missing" />Only meetings without approved minutes</label>
      </div>
      {!rows ? <Loading /> : (
        <div className="sfap-card overflow-x-auto">
          <table className="w-full text-sm" data-testid="records-table">
            <thead className="text-left text-[11px] uppercase tracking-wider text-stone-500 bg-[#FBF8F2]"><tr>{["Meeting", "Date", "Status", "Verified", "Official attendance report", "Approved minutes"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
            <tbody>
              {list.map((m) => (
                <tr key={m.id} className="border-t border-[#F0EADC] align-top" data-testid={`record-row-${m.code}`}>
                  <td className="px-4 py-3"><Link to={`/admin/meetings/${m.id}`} className="font-semibold text-[#2D3B2D] hover:underline">{m.title}</Link><div className="text-xs font-mono text-stone-500">{m.code}{m.organizer ? ` · ${m.organizer}` : ""}</div></td>
                  <td className="px-4 py-3 whitespace-nowrap">{fmtDate(m.date)}</td>
                  <td className="px-4 py-3"><StatusBadge status={m.status} />{m.finalized_at && <div className="text-[11px] text-stone-500 mt-1">by {m.finalized_by} · {fmtTime(m.finalized_at)}</div>}</td>
                  <td className="px-4 py-3 font-bold tabular-nums">{m.verified}</td>
                  <td className="px-4 py-3"><button className="btn-sm btn-primary" onClick={() => dl(`/reports/meetings/${m.id}/attendance.pdf`, `${m.code}-attendance.pdf`)} data-testid={`records-pdf-${m.code}`}><Download className="h-4 w-4" />Attendance PDF</button></td>
                  <td className="px-4 py-3">
                    {m.approved_minutes.map((d) => (
                      <button key={d.id} className="btn-sm btn-ghost mb-1 mr-1" onClick={() => dl(`/documents/${d.id}/download`, d.original_filename)} data-testid={`records-minutes-${d.id}`}><BadgeCheck className="h-4 w-4 text-green-700" />{d.title}</button>
                    ))}
                    {!m.approved_minutes.length && <span className="text-amber-800 text-sm" data-testid={`records-missing-${m.code}`}>No approved minutes{m.pending_minutes ? ` (${m.pending_minutes} awaiting approval)` : ""}</span>}
                  </td>
                </tr>
              ))}
              {!list.length && <tr><td colSpan={6} className="px-4 py-12 text-center text-stone-400">No closed or finalized meetings</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
